<template>
  <div
    class="relative h-full overflow-hidden flex items-stretch"
    ref="readerWrapper"
  >
    <div ref="readerContent" class="relative min-w-0 flex-grow flex">
      <div
        class="relative h-full w-full flex-col sm:max-w-auto flex items-center justify-center text-center space-y-4"
        v-if="error"
      >
        <div class="text-2xl font-bold">
          {{ resolveError(error).code }} {{ resolveError(error).title }}
        </div>
        <AsyncImage src="/assets/nami/stickers/sad.png" class="max-w-64" />
        <div>{{ resolveError(error).detail }}</div>
        <NamiButton button-type="primary" href="/">Take me home</NamiButton>
      </div>
      <div
        v-else-if="loading || switchingChapters"
        class="absolute inset-0 flex items-center justify-center"
      >
        <NamiLoading />
      </div>
      <Reader2ContentPane
        v-else
        :reader-manager="readerManager"
        :reader-settings="readerSettings"
        :source-reading-direction
        ref="contentPane"
        v-model:lsProgress="lsProgress"
        :lsDragging
        @click.stop="resolveReaderClick"
      />
      <Reader2OverlayTop
        class="absolute top-0 left-0 right-0 z-10"
        :class="{ disabled: error }"
        :reader-manager="readerManager"
        v-model:menu-open="menuOpen"
        v-model:comments-open="commentsOpen"
        :solo="solo"
        :is-active="loading || immersionBreak || switchingChapters"
        @click.stop
        @return="emit('close')"
      />
      <Reader2OverlayBottom
        class="absolute bottom-0 left-0 right-0 z-10"
        :source-reading-direction
        :class="{ disabled: error }"
        :loading="loading || onPlaceholder"
        :reader-manager="readerManager"
        :is-active="
          loading || immersionBreak || switchingChapters || lsDragging
        "
        :flag
        v-model:ls-progress="lsProgress"
        @drag-start="lsDragging = true"
        @drag-end="lsDragging = false"
        @click.stop
      />
      <Reader2HintAnimation
        class="!absolute"
        v-model="showHintAnimation"
        :reading-direction="computedReadingDirection"
        :source-reading-direction="sourceReadingDirection"
        :scroll-direction="scrollDirection"
        :is-vls
      />
    </div>
    <Reader2DrawerComments
      v-if="!solo && !loading"
      class="!absolute right-0 z-[11] lg:z-10 h-full lg:h-auto lg:!relative w-full max-w-[24rem] sm:min-w-[24rem] transition-[margin,right] duration-300 border-l dark:border-l-neutral-700"
      :class="{
        'mr-[-24rem]': !commentsOpen && lg,
        'right-[-24rem]': !commentsOpen && !lg,
      }"
      :reader-manager="readerManager"
      :commentsOpen="commentsOpen"
      @close="commentsOpen = false"
    />
    <Reader2DrawerMenu
      class="!absolute right-0 z-10 h-full lg:h-auto lg:!relative w-full max-w-[24rem] sm:min-w-[24rem] transition-[margin,right] duration-300 border-l dark:border-l-neutral-700"
      :class="{
        'mr-[-24rem]': !menuOpen && lg,
        'right-[-24rem]': !menuOpen && !lg,
      }"
      :source-reading-direction
      :reader-manager="readerManager"
      :reader-settings="readerSettings"
      :solo="solo"
      @switchChapter="switchChapter"
      @next-chapter="nextChapter"
      @prev-chapter="prevChapter"
      @reading-updated="showHintAnimation = true"
      :nav-loading="switchingChapters || nextPending || prevPending"
    />
  </div>
</template>

<script setup lang="ts">
import { ReaderManager } from "~/src/reader/managers/reader";
import type { UnwrapNestedRefs } from "vue";
import {
  getRelationship,
  isExpanded,
  isOrg,
  isTitle,
  type TitleReadingMode,
} from "~/src/api";
import type { ReaderSettingsInterface } from "~/src/reader/static";
import type { Reader2ContentPane } from "#components";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import { getReaderTitle } from "~/src/reader/managers/title";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { promptSubscriptionLocked } from "~/src/reader/ux";

if (process.server)
  throw new Error(
    "The Reader component must be exclusively called in the client. Wrap the component inside a ClientOnly component",
  );

interface Props {
  sourceReadingDirection: TitleReadingMode;
  readerManager: UnwrapNestedRefs<ReaderManager>;
  readerSettings: UnwrapNestedRefs<ReaderSettingsInterface>;
  flag?: {
    new: boolean;
    chapterId: string;
    sourceUrl: string;
    highUrl: string;
    mediumUrl: string;
    lowUrl: string;
  };
  pending?: boolean;
  error?: any;
  solo?: boolean;
}

const emit = defineEmits<{
  (e: "close"): void;
}>();
const {
  readerManager,
  pending,
  readerSettings,
  error,
  solo,
  sourceReadingDirection,
} = defineProps<Props>();
const route = useRoute();
const { lg } = useBreakpoints();
const { menuOpen, commentsOpen, scrollDirection, readingDirection, _isZoomed } =
  toRefs(readerSettings);

const contentPane = ref<InstanceType<typeof Reader2ContentPane>>();

const currentChapterId = computed(() => readerManager.getCurrChapterId());

// Entity resources
const queryClient = useQueryClient();
const { data: chapterData, isPending: chapterPending } = getReaderChapter(
  () => queryClient,
  currentChapterId,
);
const fetchedTitleId = computed(
  () => chapterData.value?.relationships.find(isTitle)?.id,
);
const { data: title } = getReaderTitle(() => queryClient, fetchedTitleId);
const organization = computed(() => {
  const found = title.value?.relationships.find(isOrg);
  if (found && isExpanded(found)) return found;
  return null;
});

// Chapter access
const accessIds = computed(() => readerManager.chapterIdSequence);
const { data: chapterLocks, isPending: locksPending } = useQuery(
  useChapterAccess(accessIds, "chapter"),
);
const { data: accessLocks, isPending: accessPending } = useQuery(
  useChapterAccess(accessIds, "chapter", getTokenOrThrow),
);

const loading = computed(() => pending);

const _showHintAnimation = ref(false);
const showHintAnimation = computed({
  get() {
    if (pending || error) return false;
    if (!readerManager.accessMap.get(currentChapterId.value)?.access)
      return false;

    return _showHintAnimation.value;
  },
  set(v) {
    _showHintAnimation.value = v;
    loadChapterImages(currentChapterId.value);
  },
});

const isVls = computed(() => sourceReadingDirection === "vls");
const onPlaceholder = computed(
  () =>
    readerManager.pageArray.at(readerManager.currentPageIndex)?.id ===
    "placeholder",
);

const immersionBreak = ref(false);
const computedReadingDirection = computed<"ltr" | "rtl">(() => {
  if (scrollDirection.value === "vertical") return "ltr";
  if (readingDirection.value !== "content") return readingDirection.value;
  if (sourceReadingDirection === "vls") return "ltr";
  return sourceReadingDirection;
});

// chapter navigation functions
const { action: switchChapter, pending: switchingChapters } = useAction2(
  async (chapterId: string) => {
    if (!readerManager.chapterIdSequence.length) return;
    if (!chapterLocks.value) return;

    // no access and it isnt open to public
    if (!accessLocks.value?.[chapterId] && !chapterLocks.value[chapterId]) {
      const result = await promptSubscriptionLocked();

      if (!title.value || !result) return;
      if (result === "subscribe" && organization.value) {
        navigateTo({
          path: `${linkTo(organization.value)}/subscriptions`,
          query: {
            returnTo: encodeURIComponent(route.fullPath),
          },
        });
      }

      return;
    }

    return contentPane.value?.switchChapter(chapterId);
  },
);

const { action: nextChapter, pending: nextPending } = useAction2(async () =>
  contentPane.value?.nextChapter(),
);
const { action: prevChapter, pending: prevPending } = useAction2(async () =>
  contentPane.value?.prevChapter(),
);

async function loadChapterImages(chapterId: string) {
  await readerManager.loadChapterPages(chapterId);
}

// end chapter navigation functions

// reader click
const readerContent = ref<HTMLDivElement>();

const hDividerStartPercentage = 0.4;
const hDividerEndPercentage = 0.6;
const vDividerStartPercentage = 0.4;
const vDividerEndPercentage = 0.6;

function doScrollUp() {
  contentPane.value?.prevPage();
}

function doScrollDown() {
  contentPane.value?.nextPage();
}

function doPageLeft() {
  if (computedReadingDirection.value === "ltr") {
    contentPane.value?.prevPage();
  } else contentPane.value?.nextPage();
}

function doPageRight() {
  if (computedReadingDirection.value === "ltr") {
    contentPane.value?.nextPage();
  } else contentPane.value?.prevPage();
}

const singleTapDelay = 150;
let singleTapDelayTimeout: NodeJS.Timeout | null = null;

function resolveReaderClick(event: MouseEvent | TouchEvent) {
  if (isVls.value) {
    handleReaderSingleClick(event);
    return;
  }

  // If double tap (second single tap within singleTapDelay ms) cancel it
  if (singleTapDelayTimeout !== null) {
    clearTimeout(singleTapDelayTimeout);
    singleTapDelayTimeout = null;
    return;
  }

  singleTapDelayTimeout = setTimeout(() => {
    handleReaderSingleClick(event);
    singleTapDelayTimeout = null;
  }, singleTapDelay);
}

function handleReaderSingleClick(event: MouseEvent | TouchEvent) {
  if (!readerContent.value) return;
  if (_isZoomed.value) return;

  const contentRect = readerContent.value.getBoundingClientRect();
  const { width, height } = contentRect;
  let tapX, tapY;

  if (event instanceof MouseEvent) {
    tapX = event.clientX;
    tapY = event.clientY;
  } else {
    tapX = event.touches[0].clientX;
    tapY = event.touches[0].clientY;
  }

  // normalize to reader content
  const relativeX = tapX - contentRect.x;
  const relativeY = tapY - contentRect.y;

  // compute action based on location of click
  // the click boxes will look something like this:
  //    vds vde
  // |-----------|
  // |t/l| t |t/r|
  // |---|---|---| hds
  // | l | m | r |
  // |---|---|---| hde
  // |b/l| b |b/r|
  // |-----------|

  // top
  if (
    relativeY < height * hDividerStartPercentage &&
    (scrollDirection.value === "vertical" || isVls.value)
  ) {
    doScrollUp();
  }
  // bottom
  else if (
    relativeY > height * hDividerEndPercentage &&
    (scrollDirection.value === "vertical" || isVls.value)
  ) {
    doScrollDown();
  }
  // left
  else if (
    relativeX < width * vDividerStartPercentage &&
    scrollDirection.value === "horizontal"
  ) {
    doPageLeft();
  }
  // right
  else if (
    relativeX > width * vDividerEndPercentage &&
    scrollDirection.value === "horizontal"
  ) {
    doPageRight();
  }
  // middle
  else {
    immersionBreak.value = !immersionBreak.value;
  }
}

// reader scroll
const lsProgress = ref(0);
const lsDragging = ref(false);

// reader keybinds
const keys = new Map<string, (...args: any[]) => void>();
keys.set("ArrowLeft", doPageLeft);
keys.set("ArrowRight", doPageRight);

keys.set("ArrowUp", doScrollUp);
keys.set("ArrowDown", doScrollDown);

keys.set("KeyA", doPageLeft);
keys.set("KeyD", doPageRight);

keys.set("KeyW", doScrollUp);
keys.set("KeyS", doScrollDown);

keys.set("KeyM", () => readerSettings.toggleMenu());
keys.set("KeyC", () => readerSettings.toggleComments());

function handleKeyPress(e: KeyboardEvent) {
  // ignore changes when reader is pending or hint animation is showing
  if (pending) return;
  if (onPlaceholder.value) return;
  if (showHintAnimation.value) return;

  if (document.activeElement !== document.body) return;
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;

  if (keys.has(e.code)) keys.get(e.code)?.call(null, e);
}

// stuff
onMounted(() => {
  document.addEventListener("keydown", handleKeyPress);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeyPress);
});

watch(
  () => pending,
  (v) => {
    if (!v) {
      _showHintAnimation.value = !readerSettings.hideReadingHint;
      if (readerManager.lockedMap.has(currentChapterId.value))
        _showHintAnimation.value = false;
    }
  },
);

watch(
  () => sourceReadingDirection,
  (v) => (readerSettings.titleReadingMode = v ?? "ltr"),
  { immediate: true },
);

watch(currentChapterId, (v, old) => {
  if (pending) return;
  if (v !== old && v) loadChapterImages(v);
});
</script>

<style scoped lang="postcss"></style>
